// src/components/common/TypewriterText.tsx
import React, { useState, useEffect, useRef } from 'react';

interface TypewriterTextProps {
  text: string;
  speed?: number;
  delay?: number;
  className?: string;
  onComplete?: () => void;
  showCursor?: boolean;
  autoStart?: boolean;
  skipAnimation?: boolean; // 跳過動畫，直接顯示完整文字
}

const TypewriterText: React.FC<TypewriterTextProps> = ({
  text,
  speed = 30,
  delay = 0,
  className = "",
  onComplete,
  showCursor = false,
  autoStart = true,
  skipAnimation = false,
}) => {
  const [displayText, setDisplayText] = useState(skipAnimation ? text : "");
  const [currentIndex, setCurrentIndex] = useState(skipAnimation ? text.length : 0);
  const [startTyping, setStartTyping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 捲動到底部的函數
  const scrollToBottom = () => {
    if (containerRef.current) {
      const scrollHeight = containerRef.current.scrollHeight;
      containerRef.current.scrollTop = scrollHeight;
    }
  };
  
  useEffect(() => {
    if (skipAnimation) {
      setDisplayText(text);
      setCurrentIndex(text.length);
      setStartTyping(false);
      return;
    }

    setDisplayText("");
    setCurrentIndex(0);
    setStartTyping(false);

    if (!autoStart || !text) return;
    
    // 設置延遲開始
    const delayTimer = setTimeout(() => {
      setStartTyping(true);
    }, delay);

    return () => clearTimeout(delayTimer);
  }, [text, delay, autoStart, skipAnimation]);

  // 每當顯示文本更新時，確保捲動到底部
  useEffect(() => {
    // 使用 requestAnimationFrame 確保在瀏覽器重繪後執行捲動
    // 這樣可以確保在 DOM 更新完成後捲動
    requestAnimationFrame(() => {
      // 再次使用 setTimeout 確保在所有佇列任務完成後執行
      setTimeout(() => {
        scrollToBottom();
      }, 0);
    });
  }, [displayText]);

  useEffect(() => {
    if (!startTyping) return;
    
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(currentIndex + 1);
      }, speed);
      
      return () => clearTimeout(timer);
    } else if (currentIndex >= text.length && text.length > 0) {
      // 打字完成，調用回調
      onComplete?.();
    }
  }, [currentIndex, text, speed, startTyping, onComplete]);

  // 使用 MutationObserver 監視內容變化
  useEffect(() => {
    if (!containerRef.current) return;
    
    const observer = new MutationObserver(() => {
      scrollToBottom();
    });
    
    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
      characterData: true
    });
    
    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={containerRef} 
      className={`overflow-y-auto h-full ${className}`}
      style={{ scrollBehavior: 'smooth' }}
    >
      <div className="whitespace-pre-wrap">
        {displayText}
        {showCursor && (
          <span className="animate-pulse">|</span>
        )}
      </div>
    </div>
  );
};

export default TypewriterText;